import React, { memo, useState, useCallback } from 'react';

import { black, lightGray } from 'utils/style/colors';

import {
  CollapsibleContainer,
  CollapsibleHeader,
  CollapsibleTitleIcon,
} from './styles';
import Chevron from 'components/Chevron';
import IconTypes from 'components/Icon/IconTypes';
import CollapsibleBody from './CollapsibleBody';

interface ICollapsibleProps {
  headerBackgroundColor?: string;
  bordered?: boolean;
  children: React.ReactNode;
  customHeaderStyle?: React.CSSProperties;
  headerContentColor?: string;
  defaultOpen?: boolean;
  isOpen?: boolean;
  noZ?: boolean;
  iconName?: IconTypes;
  onOpen?: () => any;
  onClose?: () => any;
  header: React.ReactElement | string;
  customBodyStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  customCollapsibleHeaderStyle?: React.CSSProperties;
  checkHeightAgain?: number;
  headerWrapperPadding?: string;
  nested?: boolean;
  noColor?: boolean;
  arrowLocation?: 'start' | 'end';
  hideChevron?: boolean;
  showOverflow?: boolean;
}

const Collapsible: React.FC<ICollapsibleProps> = ({
  customHeaderStyle,
  headerBackgroundColor = lightGray,
  bordered = true,
  children,
  headerContentColor = black,
  defaultOpen = false,
  iconName,
  header,
  isOpen,
  onOpen,
  customBodyStyle,
  checkHeightAgain,
  headerWrapperPadding,
  style,
  nested,
  noZ,
  customCollapsibleHeaderStyle,
  onClose,
  noColor,
  hideChevron,
  showOverflow = false,
  arrowLocation = 'end',
}) => {
  const [stateIsOpen, setOpen] = useState<boolean>(defaultOpen || false);

  const toggleCollapseCallback = useCallback(() => {
    setOpen((prevIsOpen) => {
      const newIsOpen = !prevIsOpen;
      if (newIsOpen) onOpen && onOpen();
      else onClose && onClose();
      return newIsOpen;
    });
  }, [onOpen, onClose]);

  const finalIsOpen = isOpen === undefined ? stateIsOpen! : isOpen;
  return (
    <CollapsibleContainer
      bordered={bordered}
      style={style}
      noZ={noZ}
      noColor={noColor}
    >
      <CollapsibleHeader
        noColor={noColor}
        backgroundColor={headerBackgroundColor}
        headerWrapperPadding={headerWrapperPadding}
        bordered={bordered}
        contentColor={headerContentColor}
        isOpen={finalIsOpen}
        onClick={toggleCollapseCallback}
        style={customCollapsibleHeaderStyle}
      >
        {arrowLocation === 'start' && (
          <Chevron isOpen={finalIsOpen!} fill={headerContentColor} />
        )}
        <div style={customHeaderStyle && customHeaderStyle}>
          {iconName && (
            <CollapsibleTitleIcon color={headerContentColor} type={iconName} />
          )}
          {header}
        </div>
        {!hideChevron && arrowLocation === 'end' && (
          <Chevron isOpen={finalIsOpen} fill={headerContentColor} />
        )}
      </CollapsibleHeader>
      {finalIsOpen && (
        <CollapsibleBody
          isOpen={finalIsOpen}
          customBodyStyle={customBodyStyle}
          checkHeightAgain={checkHeightAgain}
          nested={nested}
          showOverflow={showOverflow}
        >
          {children}
        </CollapsibleBody>
      )}
    </CollapsibleContainer>
  );
};

export default memo(Collapsible);
